import { mat3Translation } from '../core/math';
import { isGroup, locateLayer } from '../core/layerTree';
import type { Layer, Rect } from '../core/types';
import {
  activeDocument,
  layerContentBounds,
  nudgeLayerOffset,
  selectionBounds,
} from './actions';
import { moveTextLayer } from './textActions';
import { transformShapeLayer } from './vectorActions';

export type AlignEdge =
  | 'left'
  | 'centerX'
  | 'right'
  | 'top'
  | 'centerY'
  | 'bottom';

export const ALIGN_LABELS: Record<AlignEdge, string> = {
  left: 'Left Edges',
  centerX: 'Horizontal Centers',
  right: 'Right Edges',
  top: 'Top Edges',
  centerY: 'Vertical Centers',
  bottom: 'Bottom Edges',
};

export type DistributeAxis = 'horizontal' | 'vertical';

/** Offset that puts `box` against the chosen edge of `frame`. */
export function alignOffset(box: Rect, frame: Rect, edge: AlignEdge): { x: number; y: number } {
  switch (edge) {
    case 'left':
      return { x: frame.x - box.x, y: 0 };
    case 'right':
      return { x: frame.x + frame.width - (box.x + box.width), y: 0 };
    case 'centerX':
      return { x: frame.x + frame.width / 2 - (box.x + box.width / 2), y: 0 };
    case 'top':
      return { x: 0, y: frame.y - box.y };
    case 'bottom':
      return { x: 0, y: frame.y + frame.height - (box.y + box.height) };
    case 'centerY':
      return { x: 0, y: frame.y + frame.height / 2 - (box.y + box.height / 2) };
  }
}

/** Moves a layer by whole pixels, as geometry for a shape/text and as offset otherwise. */
function shiftLayer(layerId: string, dx: number, dy: number): boolean {
  const x = Math.round(dx);
  const y = Math.round(dy);
  if (x === 0 && y === 0) return false;
  if (transformShapeLayer(layerId, mat3Translation(x, y), 'Align Layer')) return true;
  if (moveTextLayer(layerId, x, y, 'Align Layer')) return true;
  return nudgeLayerOffset(layerId, x, y, 'Align Layer');
}

/**
 * Layer - Align. The frame is the selection when there is one and the canvas
 * otherwise, which is how Photoshop picks it with a single layer selected.
 */
export function alignActiveLayer(edge: AlignEdge): void {
  const doc = activeDocument();
  if (!doc?.activeLayerId) return;
  const box = layerContentBounds(doc.activeLayerId);
  if (!box) return;
  const frame = selectionBounds() ?? { x: 0, y: 0, width: doc.width, height: doc.height };
  const offset = alignOffset(box, frame, edge);
  shiftLayer(doc.activeLayerId, offset.x, offset.y);
}

/** Layers that sit next to the active one, which is the set Distribute spreads. */
function siblingLayers(): Layer[] {
  const doc = activeDocument();
  if (!doc?.activeLayerId) return [];
  const location = locateLayer(doc.layers, doc.activeLayerId);
  if (!location) return [];
  const parent = location.parent;
  return parent && isGroup(parent) ? parent.children : doc.layers;
}

/**
 * How far each box has to travel along `axis` so the gaps between them come out
 * equal. The outermost two stay where they are, as they do in Photoshop, so they
 * get an offset of zero.
 */
export function distributeOffsets<T extends { box: Rect }>(
  entries: readonly T[],
  axis: DistributeAxis,
): { entry: T; delta: number }[] {
  if (entries.length < 3) return [];

  const horizontal = axis === 'horizontal';
  const start = (box: Rect): number => (horizontal ? box.x : box.y);
  const extent = (box: Rect): number => (horizontal ? box.width : box.height);
  const sorted = [...entries].sort((a, b) => start(a.box) - start(b.box));

  const first = sorted[0].box;
  const last = sorted[sorted.length - 1].box;
  const span = start(last) + extent(last) - start(first);
  const filled = sorted.reduce((total, entry) => total + extent(entry.box), 0);
  const gap = (span - filled) / (sorted.length - 1);

  const moves: { entry: T; delta: number }[] = [];
  let cursor = start(first) + extent(first) + gap;
  for (const entry of sorted.slice(1, -1)) {
    moves.push({ entry, delta: cursor - start(entry.box) });
    cursor += extent(entry.box) + gap;
  }
  return moves;
}

/** Layer - Distribute for the layers beside the active one. */
export function distributeLayers(axis: DistributeAxis): void {
  const entries: { id: string; box: Rect }[] = [];
  for (const layer of siblingLayers()) {
    const box = layerContentBounds(layer.id);
    if (box) entries.push({ id: layer.id, box });
  }

  const horizontal = axis === 'horizontal';
  for (const { entry, delta } of distributeOffsets(entries, axis)) {
    shiftLayer(entry.id, horizontal ? delta : 0, horizontal ? 0 : delta);
  }
}
